/**
 * Isomorphic blog reads used by the /blog/$slug route loader.
 *
 * These run on the server during SSR (so the article HTML is in the initial
 * response for crawlers) and are reused from the React Query cache on the
 * client, so there is no second fetch after hydration.
 */
import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  content_html: string | null;
  key_takeaways: string[] | null;
  cover_image_url: string | null;
  category: string;
  tags: string[];
  published_at: string;
  updated_at: string;
  author_id: string;
  author_name: string | null;
  meta_title: string | null;
  meta_description: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  /** Optional totalTime/tool/supply merged into this post's auto-generated HowTo JSON-LD. */
  howto_meta: { totalTime?: string; tool?: string[]; supply?: string[] } | null;
}

export interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  cover_image_url: string | null;
  category: string;
  published_at: string;
}

export interface BlogPostBundle {
  post: BlogPostData | null;
  related: RelatedPost[];
  editorPicks: RelatedPost[];
}

const RELATED_COLUMNS = "id, title, slug, cover_image_url, category, published_at";

export async function fetchBlogPostBundle(slug: string): Promise<BlogPostBundle> {
  const { data } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();

  const post = (data as BlogPostData | null) ?? null;
  if (!post) return { post: null, related: [], editorPicks: [] };

  const [relatedRes, picksRes] = await Promise.all([
    supabase
      .from("blog_posts")
      .select(RELATED_COLUMNS)
      .eq("status", "published")
      .eq("category", post.category)
      .neq("slug", post.slug)
      .order("published_at", { ascending: false })
      .limit(3),
    supabase
      .from("blog_posts")
      .select(RELATED_COLUMNS)
      .eq("status", "published")
      .eq("category", post.category)
      .neq("slug", post.slug)
      .order("published_at", { ascending: false })
      .range(3, 5),
  ]);

  return {
    post,
    related: (relatedRes.data as RelatedPost[]) || [],
    editorPicks: (picksRes.data as RelatedPost[]) || [],
  };
}

export const blogPostQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["blog-post", slug],
    queryFn: () => fetchBlogPostBundle(slug),
    staleTime: 5 * 60 * 1000,
  });
